import { interactiveCamera } from '../render/interactive-camera';
import { interactiveRotation } from '../render/interactive-rotation';
import type { CameraAttributeValues } from '../map/attribute-camera';
import { RetainedComposition } from '../render/retained-composition';
import type { CinematicSequence } from '../../../renderer/src/cinematic-sequence';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { Ride } from '../domain/ride';
import { RENDER_BRIDGE_READY_EVENT, type RideTraceRenderBridge } from '../render/render-bridge';
import type { VideoPresentation } from '../../../renderer/src/video-presentation';
import { compositionMeasurements, type CompositionElement } from '../render/preview-diagnostics';
import type { MapStill } from '../components/LayoutChooser';
import { DEFAULT_DEPTH_OF_FIELD, type DepthOfFieldSettings, type DepthOfFieldPreview } from '../../../renderer/src/depth-of-field';
import type { LiveComposition } from '../render/live-composition';

interface Session { composition?: LiveComposition | undefined; sequence?: CinematicSequence | undefined; liveRotation?: boolean; picture: RetainedComposition; bridge: RideTraceRenderBridge; document: Document; stopWatching: () => void; failed?: boolean; lastProgress: number; signature: string; focusSignature?: string; cinematicSignature?: string; compositionSignature?: string }
function releaseSession(session: Session | undefined) {
  session?.stopWatching(); session?.bridge.dispose?.(); session?.picture.dispose();
}
const delay = () => new Promise<void>(resolve => window.setTimeout(resolve, 100));
function failureMessage(failure: unknown): string {
  return failure && typeof failure === 'object' && 'message' in failure && typeof failure.message === 'string'
    ? failure.message : 'The map preview could not load.';
}

/** A persistent preview applies the latest settings without waiting for obsolete frames. */
export function useCompositionPreview(frame: RefObject<HTMLIFrameElement | null>, url: string, ride: Ride,
  progress: number, presentation: VideoPresentation | undefined, onMeasure: ((elements: CompositionElement[]) => void) | undefined,
  onStill?: (still: MapStill | undefined) => void, depthOfField?: DepthOfFieldSettings, focusPreview?: DepthOfFieldPreview, cinematicSequence?: CinematicSequence, composition?: LiveComposition, onCameraAngle?: ((angle: number | undefined) => void) | undefined, frameScheduled = false, onCameraAttributes?: ((values: CameraAttributeValues | undefined) => void) | undefined) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [acknowledged, setAcknowledged] = useState('');
  const [error, setError] = useState('');
  const session = useRef<Session | undefined>(undefined);
  const reconnect = useRef<(() => Promise<void>) | undefined>(undefined);
  const generation = useRef(0);
  const scheduled = useRef<number | undefined>(undefined);
  const desired = useRef({ ride, progress, presentation, depthOfField, focusPreview, cinematicSequence, composition });
  const callbacks = useRef({ onMeasure, onStill, onCameraAngle, onCameraAttributes });
  callbacks.current = { onMeasure, onStill, onCameraAngle, onCameraAttributes };
  desired.current = { ride, progress, presentation, depthOfField, focusPreview, cinematicSequence, composition };
  const revision = JSON.stringify([url, ride.id, ride.title, progress, presentation, depthOfField, focusPreview, cinematicSequence, composition]);
  const requested = useRef({ revision, at: performance.now() });
  if (requested.current.revision !== revision) requested.current = { revision, at: performance.now() };
  const desiredRevision = useRef(revision);
  desiredRevision.current = revision;
  const pump = useCallback(async (current: Session) => {
    if (current.failed) return;
    const next = desired.current;
    const nextRevision = desiredRevision.current;
    try {
      if (session.current === current) {
        current.picture.hold();
        const requestedAt = requested.current.at;
        const signature = JSON.stringify(next.presentation);
        const focusSignature = JSON.stringify([next.depthOfField, next.focusPreview]);
        const cinematicSignature = JSON.stringify(next.cinematicSequence) ?? '';
        const compositionSignature = JSON.stringify(next.composition) ?? '';
        const previewCanvas = current.document.querySelector<HTMLCanvasElement>('.maplibregl-canvas');
        const previewData = previewCanvas?.dataset;
        const input = current.lastProgress === next.progress && current.signature === signature && current.focusSignature === focusSignature
          ? interactiveCamera(current.sequence, next.cinematicSequence, current.composition, next.composition, next.progress,
            { rotation: Number(previewData?.cameraRotation ?? 0), tilt: Number(previewData?.cameraRequestedTilt ?? 45), roll: Number(previewData?.cameraBank ?? 0),
              altitude: Number(previewData?.cameraAltitude ?? 1200), lens: Number(previewData?.cameraLens ?? 50) }, previewData?.cameraPlatform, next.ride.points) : undefined;
        const cameraLive = input !== undefined && current.bridge.previewCamera?.(input);
        const rotation = current.lastProgress === next.progress && current.compositionSignature === compositionSignature
          && current.signature === signature && current.focusSignature === focusSignature
          ? interactiveRotation(current.sequence, next.cinematicSequence, next.progress, next.composition?.durationSeconds ?? 30,
            Number(current.document.querySelector<HTMLCanvasElement>('.maplibregl-canvas')?.dataset.cameraRotation ?? 0),
            current.document.querySelector<HTMLCanvasElement>('.maplibregl-canvas')?.dataset.cameraPlatform) : undefined;
        if (cameraLive || rotation !== undefined && current.bridge.previewRotation?.(rotation)) {
          current.liveRotation = true;
          current.picture.release();
        } else { current.bridge.previewCamera?.(undefined); current.bridge.previewRotation?.(undefined); current.liveRotation = false; }
        if (next.composition && current.compositionSignature !== compositionSignature) {
          if (!current.bridge.setComposition) throw new Error('Reload Studio to update preview settings.');
          current.bridge.setComposition(next.composition);
        }
        if (current.cinematicSignature !== cinematicSignature) {
          if (current.bridge.setCinematicSequence) current.bridge.setCinematicSequence(next.cinematicSequence);
          else if (next.cinematicSequence) throw new Error('Reload Studio to use cinematic keyframes with the current renderer.');
        }
        if (next.presentation && current.signature !== signature) current.bridge.setPresentation(next.presentation);
        if (current.focusSignature !== focusSignature) {
          if (current.bridge.setDepthOfField) current.bridge.setDepthOfField(next.depthOfField ?? DEFAULT_DEPTH_OF_FIELD,
            next.focusPreview ?? { original: false, mask: false });
          else if (next.depthOfField?.enabled) throw new Error('Reload Studio to use depth of field with the current renderer.');
        }
        current.lastProgress = next.progress;
        current.signature = signature;
        current.focusSignature = focusSignature;
        current.cinematicSignature = cinematicSignature;
        current.sequence = next.cinematicSequence;
        current.composition = next.composition;
        current.compositionSignature = compositionSignature;
        await current.bridge.renderFrame(next.progress, next.composition?.cameraGimbal ? 120_000 : 30_000);
        if (current.failed || session.current !== current || desiredRevision.current !== nextRevision) return;
        current.bridge.previewCamera?.(undefined); current.bridge.previewRotation?.(undefined); current.liveRotation = false;
        const captureStart = performance.now();
        if (!await current.picture.capture(() => !current.failed && session.current === current && desiredRevision.current === nextRevision)) return;
        const captureMs = performance.now() - captureStart;
        current.picture.release();
        const root = current.document.documentElement;
        root.dataset.previewSeekMs = String(performance.now() - requestedAt);
        root.dataset.previewPresentedAt = String(performance.now());
        root.dataset.previewProgress = String(next.progress);
        root.dataset.previewPresentedFrames = String(Number(root.dataset.previewPresentedFrames ?? 0) + 1);
        const mapCanvas = current.document.querySelector<HTMLCanvasElement>('#root .maplibregl-canvas');
        const samples = JSON.parse(root.dataset.previewSeekSamples ?? '[]') as unknown[];
        samples.push({ captureMs, ms: performance.now() - requestedAt, at: performance.now(), progress: next.progress,
          bearing: Number(mapCanvas?.dataset.cameraBearing), cameraPlatform: mapCanvas?.dataset.cameraPlatform,
          misses: Number(mapCanvas?.dataset.gimbalCacheMisses ?? 0), tileBlocked: current.bridge.lastMapFrameTiming?.tileBlocked ?? false });
        root.dataset.previewSeekSamples = JSON.stringify(samples.slice(-240));
        if (frame.current) {
          frame.current.dataset.previewSeekSamples = root.dataset.previewSeekSamples;
          frame.current.dataset.previewPresentedFrames = root.dataset.previewPresentedFrames;
        }
        if (callbacks.current.onMeasure) callbacks.current.onMeasure(compositionMeasurements(current.document));
        const canvas = current.document.querySelector<HTMLCanvasElement>('.maplibregl-canvas');
        const data = canvas?.dataset;
        const pitch = Number(canvas?.dataset.cameraPitch);
        const attributes = data ? { rotation: Number(data.cameraRotation ?? data.cameraBearing), tilt: Number(data.cameraTilt ?? data.cameraPitch), roll: Number(data.cameraBank ?? data.cameraRoll ?? 0), altitude: Number(data.cameraAltitude), lens: Number(data.cameraLens) } : undefined;
        callbacks.current.onCameraAttributes?.(attributes && Object.values(attributes).every(Number.isFinite) ? attributes : undefined);
        callbacks.current.onCameraAngle?.(canvas && Number.isFinite(pitch) ? pitch : undefined);
        if (canvas && callbacks.current.onStill) callbacks.current.onStill({ url: canvas.toDataURL('image/png'),
          attributionHtml: current.document.querySelector('.video-map-credits')?.innerHTML ?? '' });
        setAcknowledged(nextRevision);
      }
      if (!current.failed && session.current === current && desiredRevision.current === nextRevision) { setError(''); setStatus('ready'); }
    } catch (failure) {
      if (!current.failed && session.current === current && desiredRevision.current === nextRevision) { current.bridge.previewCamera?.(undefined); current.bridge.previewRotation?.(undefined); current.liveRotation = false; current.picture.hold(); setError(failureMessage(failure)); setStatus('error'); }
    }
  }, []);
  useEffect(() => {
    generation.current++;
    releaseSession(session.current); session.current = undefined;
    setStatus('loading'); setError(''); callbacks.current.onMeasure?.([]); callbacks.current.onStill?.(undefined);
    return () => { generation.current++; releaseSession(session.current); session.current = undefined; if (scheduled.current !== undefined) cancelAnimationFrame(scheduled.current); };
  }, [url, ride.id, ride.title]);
  const settingsRevision = JSON.stringify([progress, presentation, depthOfField, focusPreview, cinematicSequence, composition]);
  useEffect(() => {
    if (!session.current) return;
    if (!session.current.liveRotation) session.current.picture.hold();
    // Playback already dispatches at most once per animation frame. Manual input
    // still coalesces here; adding another RAF to playback halves warm cadence.
    if (frameScheduled) { void pump(session.current); return; }
    scheduled.current = requestAnimationFrame(() => {
      scheduled.current = undefined;
      if (session.current) void pump(session.current);
    });
    return () => { if (scheduled.current !== undefined) cancelAnimationFrame(scheduled.current); };
  }, [settingsRevision, pump, frameScheduled]);
  const onLoad = useCallback(async () => {
    const attempt = ++generation.current;
    const target = frame.current?.contentWindow;
    if (!target) return;
    try {
      const started = performance.now();
      while (!target.__rideTraceRender) {
        if (attempt !== generation.current) return;
        if (performance.now() - started > 30_000) throw new Error('The composition preview did not start. Reload the preview.');
        await delay();
      }
      if (attempt !== generation.current) return;
      const bridge = target.__rideTraceRender;
      const previewDocument = target.document;
      bridge.loadRide({ ...ride, points: ride.sourcePoints ?? ride.points });
      releaseSession(session.current);
      const lost = () => {
        if (session.current !== current) return;
        current.failed = true;
        current.picture.hold();
        current.bridge.dispose();
        setError('The map preview lost its graphics connection. Reload the preview to continue.');
        setStatus('error');
      };
      const replaced = () => {
        if (session.current !== current || target.__rideTraceRender === bridge) return;
        current.failed = true;
        setStatus('loading');
        // A renderer refresh can install its replacement inside a React effect.
        queueMicrotask(() => { if (session.current === current) void reconnect.current?.(); });
      };
      const current: Session = { picture: new RetainedComposition(previewDocument), bridge, document: previewDocument,
        stopWatching: () => {
          previewDocument.removeEventListener('webglcontextlost', lost, true);
          previewDocument.removeEventListener(RENDER_BRIDGE_READY_EVENT, replaced);
        }, lastProgress: -1, signature: '' };
      previewDocument.addEventListener('webglcontextlost', lost, true);
      previewDocument.addEventListener(RENDER_BRIDGE_READY_EVENT, replaced);
      session.current = current;
      await pump(current);
    } catch (failure) {
      if (attempt === generation.current) { setError(failureMessage(failure)); setStatus('error'); }
    }
  }, [frame, ride, pump]);
  reconnect.current = onLoad;
  return { status: status === 'ready' && acknowledged !== revision ? 'loading' as const : status, error, onLoad };
}
